import type { DiscountDto, DiscountInput, PromotionDto } from '@deli/contracts';
import { useState, type FormEvent } from 'react';
import {
  useDeleteDiscount,
  useDeletePromotion,
  useSaveDiscount,
  useSavePromotion,
} from '../api/pricing.js';
import { ErrorText } from '../components/ErrorText.js';
import { Modal } from '../components/Modal.js';
import { statusBadge } from './CustomersPage.js';
import { parseMoneyInput, toMoneyInput } from '../format.js';
import { t } from '../i18n/uk.js';
import {
  CategorySelect,
  CustomerPicker,
  PricePreview,
  ProductPicker,
  type ChosenCustomer,
  type ChosenProduct,
} from './PricingParts.js';

const tp = t.pricing;

type Kind = 'all' | 'category' | 'product';
type Mode = 'percent_off' | 'fixed_price';

/** What a discount or promotion applies to, and how much — the part both forms share. */
interface Terms {
  kind: Kind;
  categoryId: string;
  product: ChosenProduct | null;
  mode: Mode;
  value: string;
  validFrom: string;
  validTo: string;
}

const termsOf = (row?: DiscountDto | PromotionDto): Terms => ({
  kind: row?.productId ? 'product' : row?.categoryId ? 'category' : 'all',
  categoryId: row?.categoryId ?? '',
  product: row?.productId
    ? { id: row.productId, sku: row.productSku ?? '', name: row.productName ?? '' }
    : null,
  mode: row?.mode ?? 'percent_off',
  value: !row
    ? ''
    : row.mode === 'percent_off'
      ? String(row.value).replace('.', ',')
      : toMoneyInput(row.value),
  validFrom: row?.validFrom ?? '',
  validTo: row?.validTo ?? '',
});

/** The API input for the terms, or null while something is missing or not a number. */
function inputOf(terms: Terms): DiscountInput | null {
  const value =
    terms.mode === 'percent_off'
      ? Number(terms.value.trim().replace(',', '.'))
      : parseMoneyInput(terms.value);
  if (value === null || !Number.isFinite(value) || terms.value.trim() === '') return null;
  if (terms.mode === 'percent_off' && (value <= 0 || value > 100)) return null;
  if (terms.kind === 'category' && !terms.categoryId) return null;
  if (terms.kind === 'product' && !terms.product) return null;
  return {
    productId: terms.kind === 'product' ? terms.product!.id : null,
    categoryId: terms.kind === 'category' ? terms.categoryId : null,
    mode: terms.mode,
    value,
    validFrom: terms.validFrom || null,
    validTo: terms.validTo || null,
  };
}

/** Why the terms can't be sent, in the person's words. */
const termsProblem = (terms: Terms): string =>
  terms.value.trim() === '' || inputOf({ ...terms, kind: 'all' })
    ? t.pricing.preview.fixDraft
    : terms.mode === 'percent_off'
      ? t.pricing.invalidPercent
      : t.common.invalidAmount;

function TermsFields({ terms, set }: { terms: Terms; set: (next: Terms) => void }) {
  const field = (key: 'value' | 'validFrom' | 'validTo') => (e: { target: { value: string } }) =>
    set({ ...terms, [key]: e.target.value });
  return (
    <>
      <label>
        {tp.target}
        <select
          aria-label={tp.target}
          value={terms.kind}
          onChange={(e) => set({ ...terms, kind: e.target.value as Kind })}
        >
          {(['all', 'category', 'product'] as const).map((kind) => (
            <option key={kind} value={kind}>
              {tp.targets[kind]}
            </option>
          ))}
        </select>
      </label>
      {terms.kind === 'category' && (
        <CategorySelect
          value={terms.categoryId}
          onChange={(categoryId) => set({ ...terms, categoryId })}
        />
      )}
      {terms.kind === 'product' && (
        <div className="span-2">
          {terms.product && (
            <p>
              <code>{terms.product.sku}</code> {terms.product.name}
            </p>
          )}
          <ProductPicker onPick={(product) => set({ ...terms, product })} />
        </div>
      )}
      <label>
        {tp.mode}
        <select
          aria-label={tp.mode}
          value={terms.mode}
          onChange={(e) => set({ ...terms, mode: e.target.value as Mode, value: '' })}
        >
          <option value="percent_off">{tp.modes.percent_off}</option>
          <option value="fixed_price">{tp.modes.fixed_price}</option>
        </select>
      </label>
      <label>
        {terms.mode === 'percent_off' ? tp.percent : tp.price}
        <input
          aria-label={terms.mode === 'percent_off' ? tp.percent : tp.price}
          inputMode="decimal"
          required
          value={terms.value}
          onChange={field('value')}
        />
      </label>
      <label>
        {tp.validFrom}
        <input
          aria-label={tp.validFrom}
          type="date"
          value={terms.validFrom}
          onChange={field('validFrom')}
        />
      </label>
      <label>
        {tp.validTo}
        <input
          aria-label={tp.validTo}
          type="date"
          value={terms.validTo}
          onChange={field('validTo')}
        />
      </label>
    </>
  );
}

function ReasonField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <label className="span-2">
      {t.common.reason}
      <input
        aria-label={t.common.reason}
        required
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

/**
 * Add or change a partner / personal discount. `customer` fixes the customer (opened from the
 * card); editing keeps the discount's own customer.
 */
export function DiscountDialog({
  discount,
  customer: fixedCustomer,
  onClose,
}: {
  discount?: DiscountDto;
  customer?: ChosenCustomer;
  onClose: () => void;
}) {
  const save = useSaveDiscount();
  const [customer, setCustomer] = useState<ChosenCustomer | null>(
    discount
      ? {
          id: discount.customerId,
          fullName: discount.customerName ?? '—',
          type: discount.customerType ?? 'b2c',
          status: discount.customerStatus ?? undefined,
        }
      : (fixedCustomer ?? null),
  );
  const [terms, setTerms] = useState<Terms>(termsOf(discount));
  const [reason, setReason] = useState('');
  const [invalid, setInvalid] = useState(false);
  const input = inputOf(terms);
  const canPickCustomer = !discount && !fixedCustomer;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!input || !customer) {
      setInvalid(true);
      return;
    }
    const request = discount
      ? ({ id: discount.id, body: { ...input, reason } } as const)
      : ({ id: null, body: { ...input, customerId: customer.id, reason } } as const);
    save.mutate(request, { onSuccess: onClose });
  };

  return (
    <Modal title={discount ? tp.discountTitle : tp.newDiscount} onClose={onClose}>
      <form className="form-grid" onSubmit={submit}>
        <div className="span-2">
          <span className="muted">{tp.customer}: </span>
          {customer ? (
            <>
              <strong>{customer.fullName}</strong>{' '}
              <span className="muted">{t.customers.types[customer.type]}</span>{' '}
              {customer.status && customer.status !== 'active' && statusBadge(customer.status)}{' '}
              {canPickCustomer && (
                <button type="button" onClick={() => setCustomer(null)}>
                  {tp.otherCustomer}
                </button>
              )}
            </>
          ) : (
            <>
              <span className="muted">{tp.pickCustomer}</span>
              <CustomerPicker onPick={setCustomer} />
            </>
          )}
        </div>
        <TermsFields terms={terms} set={setTerms} />
        <ReasonField value={reason} onChange={setReason} />
        <div className="actions">
          <button type="submit" className="primary" disabled={save.isPending || !customer}>
            {t.common.save}
          </button>
          <button type="button" onClick={onClose}>
            {t.common.cancel}
          </button>
          {invalid && !input && <span className="error-text">{termsProblem(terms)}</span>}
        </div>
        <ErrorText error={save.error} />
      </form>
      {customer && (
        <PricePreview
          key={customer.id}
          customer={customer}
          invalid={!input}
          draft={input ? { personalDiscount: { ...input, id: discount?.id ?? null } } : undefined}
        />
      )}
    </Modal>
  );
}

/** Add or change a promotion; the preview can price it for any customer or a guest. */
export function PromotionDialog({
  promotion,
  onClose,
}: {
  promotion?: PromotionDto;
  onClose: () => void;
}) {
  const save = useSavePromotion();
  const [name, setName] = useState(promotion?.name ?? '');
  const [terms, setTerms] = useState<Terms>(termsOf(promotion));
  const [appliesTo, setAppliesTo] = useState<('b2c' | 'b2b')[]>(promotion?.appliesTo ?? ['b2c']);
  const [stackable, setStackable] = useState(promotion?.stackable ?? false);
  const [reason, setReason] = useState('');
  const [invalid, setInvalid] = useState(false);
  const input = inputOf(terms);
  const promotionInput =
    input && name.trim() && appliesTo.length > 0
      ? {
          ...input,
          name,
          scope: terms.kind,
          appliesTo,
          stackable: stackable && terms.mode === 'percent_off',
        }
      : null;

  const toggleType = (type: 'b2c' | 'b2b') =>
    setAppliesTo(
      appliesTo.includes(type) ? appliesTo.filter((x) => x !== type) : [...appliesTo, type],
    );

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!promotionInput) {
      setInvalid(true);
      return;
    }
    save.mutate(
      { id: promotion?.id ?? null, body: { ...promotionInput, reason } },
      { onSuccess: onClose },
    );
  };

  return (
    <Modal title={promotion ? tp.promotionTitle : tp.newPromotion} onClose={onClose}>
      <form className="form-grid" onSubmit={submit}>
        <label className="span-2">
          {tp.name}
          <input
            aria-label={tp.name}
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        <TermsFields terms={terms} set={setTerms} />
        <fieldset className="span-2 inline-form">
          <legend>{tp.appliesTo}</legend>
          {(['b2c', 'b2b'] as const).map((type) => (
            <label key={type} className="check">
              <input
                type="checkbox"
                checked={appliesTo.includes(type)}
                onChange={() => toggleType(type)}
              />
              {t.customers.types[type]}
            </label>
          ))}
        </fieldset>
        <label className="check span-2">
          <input
            type="checkbox"
            checked={stackable && terms.mode === 'percent_off'}
            disabled={terms.mode !== 'percent_off'}
            onChange={(e) => setStackable(e.target.checked)}
          />
          {tp.stackable}
        </label>
        <p className="muted hint">{tp.stackableHint}</p>
        <ReasonField value={reason} onChange={setReason} />
        <div className="actions">
          <button type="submit" className="primary" disabled={save.isPending}>
            {t.common.save}
          </button>
          <button type="button" onClick={onClose}>
            {t.common.cancel}
          </button>
          {invalid && !promotionInput && (
            <span className="error-text">{input ? tp.preview.fixDraft : termsProblem(terms)}</span>
          )}
        </div>
        <ErrorText error={save.error} />
      </form>
      <PricePreview
        invalid={!promotionInput}
        draft={
          promotionInput
            ? { promotion: { ...promotionInput, id: promotion?.id ?? null } }
            : undefined
        }
      />
    </Modal>
  );
}

/** "Видалити" → asks for the reason, then deletes. */
export function DeleteWithReason({
  label,
  onDelete,
  pending,
  error,
}: {
  label: string;
  onDelete: (reason: string, done: () => void) => void;
  pending: boolean;
  error: unknown;
}) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)}>
        {t.common.remove}
      </button>
    );
  }
  return (
    <span className="inline-form">
      <input
        aria-label={`${tp.deleteReason}: ${label}`}
        placeholder={tp.deleteReason}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
      <button
        type="button"
        disabled={!reason.trim() || pending}
        onClick={() => onDelete(reason, () => setOpen(false))}
      >
        {tp.confirmDelete}
      </button>
      <button type="button" onClick={() => setOpen(false)}>
        {t.common.cancel}
      </button>
      <ErrorText error={error} />
    </span>
  );
}

export const useDeleteDiscountAction = () => {
  const remove = useDeleteDiscount();
  return {
    pending: remove.isPending,
    error: remove.error,
    run: (id: string) => (reason: string, done: () => void) =>
      remove.mutate({ id, reason }, { onSuccess: done }),
  };
};

export const useDeletePromotionAction = () => {
  const remove = useDeletePromotion();
  return {
    pending: remove.isPending,
    error: remove.error,
    run: (id: string) => (reason: string, done: () => void) =>
      remove.mutate({ id, reason }, { onSuccess: done }),
  };
};
